import { observer } from "mobx-react";
import { FileLink } from "modules/common/components/files/FileLink";
import { LocalLoader } from "modules/common/components/loader/Loader";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon";
import { eat } from "modules/common/services/typescript";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { AgentMailStoreType, DELIVERY_FAILED, DELIVERY_SENT } from "../models/agent-mail-store";
import { Avatar } from "./Avatar";
import { MailBody } from "./MailBody";
import { deliveryLabel, formatDay } from "./mail-format";
import styles from "./AgentMailbox.module.scss";

interface ThreadViewProps {
    store: AgentMailStoreType;
    baseUrl: string;
    /** Форма ответа под письмами; done - свернуть её */
    renderComposer: (done: () => void) => React.ReactNode;
    onAttach: () => void;
}

export const ThreadView = observer(({ store, baseUrl, renderComposer, onAttach }: ThreadViewProps) => {
    const thread = store.currentThread;
    const posts = store.posts;
    // форма ответа свёрнута, пока не нажали «Ответить»: письмам нужно место
    const [replying, setReplying] = useState(false);
    const done = useCallback(() => setReplying(false), []);
    const scroller = useRef<HTMLDivElement>(null);

    // открыли переписку или пришло новое письмо - к последнему письму
    const lastId = posts.length > 0 ? posts[posts.length - 1].id : "";
    useEffect(() => {
        const element = scroller.current;
        if (element) {
            element.scrollTop = element.scrollHeight;
        }
    }, [lastId]);

    if (!thread) {
        return null;
    }

    return (
        <>
            <div className={styles.header}>
                <div className={styles.headerBody}>
                    <div className={styles.headerTitle}>{thread.subject || "(без темы)"}</div>
                    <div className={styles.headerMeta}>
                        {thread.order ? (
                            <span className={`${styles.chip} ${styles.chipOrder}`}>{thread.order.name}</span>
                        ) : (
                            <span className={styles.chip}>без договора</span>
                        )}
                        {thread.unassigned && (
                            <span className={`${styles.chip} ${styles.chipWarning}`}>
                                с незнакомого адреса {thread.senderEmail}
                            </span>
                        )}
                        {thread.agents.map((agent) => (
                            <span key={agent.id} className={styles.chip} title={agent.email ?? ""}>
                                {agent.name}
                            </span>
                        ))}
                        {thread.tag && (
                            <span
                                className={`${styles.chip} ${styles.chipMuted}`}
                                title="Метка переписки в теме письма"
                            >
                                #{thread.tag}
                            </span>
                        )}
                    </div>
                </div>
                <PlanrButton type="lightblueish" size="small" icon="general-attach" onClick={onAttach}>
                    {thread.unassigned ? "Привязать" : thread.order ? "Сменить договор" : "К договору"}
                </PlanrButton>
            </div>

            <div className={styles.posts} ref={scroller}>
                {store.loadingPosts && posts.length === 0 && <LocalLoader active />}
                {posts.map((post, index) => (
                    <Fragment key={post.id}>
                        {(index === 0 || posts[index - 1].date !== post.date) && (
                            <div className={styles.dayDivider}>{formatDay(post.date)}</div>
                        )}
                        <Post store={store} id={post.id} baseUrl={baseUrl} threadSubject={thread.subject ?? ""} />
                    </Fragment>
                ))}
            </div>

            {thread.unassigned ? (
                <div className={styles.replyBar}>
                    <span className={styles.replyHint}>
                        Письмо пришло с адреса, которого нет среди агентов. Привяжите переписку к агенту, чтобы
                        ответить.
                    </span>
                </div>
            ) : replying ? (
                renderComposer(done)
            ) : (
                <div className={styles.replyBar}>
                    <PlanrButton type="greenish" size="small" icon="general-mail" onClick={() => setReplying(true)}>
                        Ответить
                    </PlanrButton>
                    <span className={styles.replyHint}>Ответ уйдёт каждому получателю отдельным письмом</span>
                </div>
            )}
        </>
    );
});

/**
 * Письмо цепочки. Узел берём из стора по id, а не пропом: при перезагрузке
 * цепочки прежние узлы заменяются (см. Rost.Ui/CLAUDE.md)
 */
// «Re:», «Fwd:», «Ответ:» и метка переписки - тема без них совпадает с темой переписки
const cleanSubject = (subject: string) =>
    subject
        .replace(/\[#[A-Z0-9]{6}\]/g, "")
        .replace(/^\s*((re|fw|fwd|ответ|отв|пересл)\s*(\[\d+\])?\s*:\s*)+/i, "")
        .trim();

const Post = observer(
    ({
        store,
        id,
        baseUrl,
        threadSubject,
    }: {
        store: AgentMailStoreType;
        id: string;
        baseUrl: string;
        threadSubject: string;
    }) => {
        const post = store.posts.find((p) => p.id === id);

        if (!post) {
            return null;
        }

        const outgoing = post.isRequest;
        const author = outgoing
            ? (post.employer?.name ?? "Сотрудник")
            : post.recipients[0]?.name || post.senderName || post.senderEmail || "Отправитель";

        return (
            <div className={`${styles.postRow} ${outgoing ? styles.postRowOut : ""}`}>
                <Avatar name={author} small />
                <div
                    className={`${styles.post} ${outgoing ? styles.postOut : ""} ${post.unread ? styles.postUnread : ""}`}
                >
                    <div className={styles.postHead}>
                        <span className={styles.postAuthor}>{author}</span>
                        {!outgoing && post.senderEmail && (
                            <span className={styles.postAddress}>{post.senderEmail}</span>
                        )}
                        {outgoing && post.recipients.length > 0 && (
                            <span className={styles.postAddress}>
                                → {post.recipients.map((r) => r.name).join(", ")}
                            </span>
                        )}
                        <span className={styles.postTime}>{post.time}</span>
                    </div>
                    {post.subject && cleanSubject(post.subject) !== cleanSubject(threadSubject) && (
                        <div className={styles.postSubject}>{post.subject}</div>
                    )}

                    <MailBody html={post.message ?? ""} />

                    {post.attachments.length > 0 && (
                        <div className={styles.files}>
                            {post.attachments.map((file) => (
                                <FileLink
                                    key={file.id}
                                    baseUrl={baseUrl}
                                    file={{ fileId: file.id, fileName: file.name }}
                                    readOnly
                                    onRemove={eat}
                                />
                            ))}
                        </div>
                    )}

                    {outgoing && post.deliveries.length > 0 && (
                        <div className={styles.deliveries}>
                            {post.deliveries.map((delivery) => {
                                const sent = delivery.status === DELIVERY_SENT;
                                const failed = delivery.status === DELIVERY_FAILED;

                                return (
                                    <span
                                        key={delivery.email}
                                        className={`${styles.delivery} ${
                                            sent
                                                ? styles.deliverySent
                                                : failed
                                                  ? styles.deliveryFailed
                                                  : styles.deliveryPending
                                        }`}
                                        title={`${delivery.email}: ${deliveryLabel(delivery.status, delivery.error)}`}
                                    >
                                        <GeneralIcon
                                            type={
                                                sent
                                                    ? "general-check"
                                                    : failed
                                                      ? "general-exclamation"
                                                      : "general-clock"
                                            }
                                            size={11}
                                        />
                                        {delivery.name || delivery.email}
                                        {failed && (
                                            <button
                                                type="button"
                                                className={styles.resend}
                                                onClick={() => store.resend(post.id, delivery.email)}
                                            >
                                                отправить заново
                                            </button>
                                        )}
                                    </span>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        );
    }
);
